// src/components/settings/JobReminderSettings.js
import React, { useState, useEffect } from 'react';
import { db } from '../../firebase/config';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { Bell, Info } from 'lucide-react';

const REMINDER_MODE_LABELS = {
    '2_weeks':         '2 weeks before the job',
    '3_weeks':         '3 weeks before the job',
    '1st_prior_month': '1st of the prior month',
    'custom':          'Custom (days before)',
};

const DEFAULT_CUSTOMER_EMAIL_BODY =
`Hello {customerName},

Just a friendly reminder that your upcoming job "{jobTitle}" is scheduled for {jobDate}.

If you have any questions or need to make changes, please don't hesitate to reach out.

We look forward to seeing you!

{companyName}`;

const JobReminderSettings = ({ currentUserProfile }) => {
    const orgId = currentUserProfile?.organizationId;

    // ── Internal reminder (in-app notification) ──────────────────────────────
    const [mode, setMode]               = useState('2_weeks');
    const [customDays, setCustomDays]   = useState(14);

    // ── Customer auto-email ───────────────────────────────────────────────────
    const [customerEmailEnabled, setCustomerEmailEnabled] = useState(false);
    const [customerEmailBody, setCustomerEmailBody]       = useState(DEFAULT_CUSTOMER_EMAIL_BODY);

    // ── UI state ──────────────────────────────────────────────────────────────
    const [isSaving, setIsSaving]       = useState(false);
    const [isLoading, setIsLoading]     = useState(true);
    const [saveMessage, setSaveMessage] = useState('');
    // j1-loadfail-1f6b8c34 — the catch below logged and then cleared isLoading, so a
    // failed read rendered the form with hardcoded defaults and one Save wrote them
    // over the real settings. Fail closed instead.
    const [loadError, setLoadError]     = useState(false);

    // ── Load existing settings ────────────────────────────────────────────────
    useEffect(() => {
        if (!orgId) { setIsLoading(false); return; }
        const load = async () => {
            setIsLoading(true);
            // j1-dotsave-5a3c7e21 — reset to defaults on an org switch (God mode swaps
            // org in place), so one org's settings can't be saved into another.
            setMode('2_weeks');
            setCustomDays(14);
            setCustomerEmailEnabled(false);
            setCustomerEmailBody(DEFAULT_CUSTOMER_EMAIL_BODY);
            setLoadError(false);
            try {
                const snap = await getDoc(doc(db, `organizations/${orgId}`));
                if (snap.exists()) {
                    const s = snap.data().jobReminderSettings;
                    if (s) {
                        setMode(s.mode || '2_weeks');
                        setCustomDays(s.customDays ?? 14);
                        setCustomerEmailEnabled(s.customerEmailEnabled ?? false);
                        setCustomerEmailBody(s.customerEmailBody || DEFAULT_CUSTOMER_EMAIL_BODY);
                    }
                }
            } catch (err) {
                console.error('Failed to load reminder settings:', err);
                setLoadError(true);
            } finally {
                setIsLoading(false);
            }
        };
        load();
    }, [orgId]);

    const handleSave = async () => {
        if (!orgId) return;
        // j1-loadfail-1f6b8c34 — never write settings that were never loaded.
        if (loadError) {
            setSaveMessage('Settings could not be loaded, so they cannot be saved. Please reload the page.');
            return;
        }
        setIsSaving(true);
        setSaveMessage('');
        try {
            // j1-dotsave-5a3c7e21 — dotted field paths instead of a whole
            // `jobReminderSettings` map rebuilt from a snapshot taken when the screen
            // opened, which reverted any key another admin had changed since. Dotted
            // paths REQUIRE updateDoc (setDoc would create a field literally named
            // "jobReminderSettings.mode"); the organization document always exists by
            // the time this screen renders.
            await updateDoc(doc(db, `organizations/${orgId}`), {
                'jobReminderSettings.mode': mode,
                'jobReminderSettings.customDays': mode === 'custom' ? parseInt(customDays) || 14 : 14,
                'jobReminderSettings.customerEmailEnabled': customerEmailEnabled,
                'jobReminderSettings.customerEmailBody': customerEmailBody,
            });
            setSaveMessage('Settings saved successfully.');
            setTimeout(() => setSaveMessage(''), 3000);
        } catch (err) {
            console.error('Failed to save reminder settings:', err);
            setSaveMessage('Error saving settings. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    // Live preview of calculated reminder date
    const getPreviewText = () => {
        const exampleJobDate = new Date();
        exampleJobDate.setDate(exampleJobDate.getDate() + 30);
        let reminderDate;
        if (mode === '1st_prior_month') {
            reminderDate = new Date(exampleJobDate.getFullYear(), exampleJobDate.getMonth() - 1, 1);
        } else if (mode === '3_weeks') {
            reminderDate = new Date(exampleJobDate.getTime() - 21 * 86400000);
        } else if (mode === 'custom') {
            reminderDate = new Date(exampleJobDate.getTime() - (parseInt(customDays) || 14) * 86400000);
        } else {
            reminderDate = new Date(exampleJobDate.getTime() - 14 * 86400000);
        }
        const fmt = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        return `e.g. Job on ${fmt(exampleJobDate)} → reminder on ${fmt(reminderDate)}`;
    };

    if (isLoading) return <div className="p-6 text-sm text-gray-500">Loading reminder settings...</div>;

    // j1-loadfail-1f6b8c34 — show an error instead of a form full of defaults.
    if (loadError) {
        return (
            <div className="p-6">
                <h2 className="text-xl font-bold text-red-700">Couldn't load reminder settings</h2>
                <p className="text-gray-600 mt-2">
                    Your current reminder settings could not be read, so they can't be edited right
                    now — saving would overwrite them with defaults. Please reload the page, and if
                    this keeps happening, contact support.
                </p>
            </div>
        );
    }

    return (
        <div className="p-6 max-w-2xl space-y-8">

            {/* ── Header ──────────────────────────────────────────────────── */}
            <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                    <Bell size={20} className="text-blue-600" />
                </div>
                <div>
                    <h2 className="text-xl font-bold text-gray-800">Job Confirmation Reminders</h2>
                    <p className="text-sm text-gray-500">Configure when reminders fire and whether to auto-email the customer.</p>
                </div>
            </div>

            {/* ── Section 1: Internal reminder timing ─────────────────────── */}
            <div className="border-t pt-6 space-y-4">
                <h3 className="text-lg font-semibold text-gray-800">Internal Reminder Timing</h3>

                <div className="flex gap-3 bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
                    <Info size={16} className="mt-0.5 shrink-0 text-blue-500" />
                    <div>
                        <p className="font-semibold mb-1">How it works</p>
                        <p>When a job is saved, a reminder date is calculated from the job's start date using the rule below. Each morning the system checks for jobs whose reminder date is today and sends an in-app notification to all admins and managers. You can also override the date per-job inside the Job form.</p>
                    </div>
                </div>

                <div className="space-y-3">
                    {Object.entries(REMINDER_MODE_LABELS).map(([value, label]) => (
                        <label
                            key={value}
                            className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                                mode === value
                                    ? 'border-blue-500 bg-blue-50 text-blue-800'
                                    : 'border-gray-200 bg-white hover:border-gray-300 text-gray-700'
                            }`}
                        >
                            <input
                                type="radio"
                                name="reminderMode"
                                value={value}
                                checked={mode === value}
                                onChange={() => setMode(value)}
                                className="h-4 w-4 text-blue-600 border-gray-300"
                            />
                            <span className="text-sm font-medium">{label}</span>
                        </label>
                    ))}
                </div>

                {mode === 'custom' && (
                    <div className="ml-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Days before the job</label>
                        <div className="flex items-center gap-3">
                            <input
                                type="number"
                                min="1"
                                max="365"
                                value={customDays}
                                onChange={(e) => setCustomDays(e.target.value)}
                                className="w-24 p-2 border rounded-md text-sm"
                            />
                            <span className="text-sm text-gray-500">days before the job start date</span>
                        </div>
                    </div>
                )}

                <p className="text-xs text-gray-400 italic flex items-center gap-1.5 ml-1">
                    <Bell size={12} />
                    {getPreviewText()}
                </p>
            </div>

            {/* ── Section 2: Customer auto-email ──────────────────────────── */}
            <div className="border-t pt-6 space-y-4">
                <h3 className="text-lg font-semibold text-gray-800">Customer Confirmation Email</h3>
                <p className="text-sm text-gray-500">
                    Optionally send the customer an automatic email on the same day the internal reminder fires. Uses your SendGrid account, exactly like overdue invoice emails.
                </p>

                {/* Toggle — matches AutomationSettings pattern exactly */}
                <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={customerEmailEnabled}
                        onChange={(e) => setCustomerEmailEnabled(e.target.checked)}
                        className="h-4 w-4 rounded"
                    />
                    <span>Enable automatic customer confirmation emails.</span>
                </label>

                {customerEmailEnabled && (
                    <div className="space-y-3">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Email Body
                            </label>
                            <textarea
                                value={customerEmailBody}
                                onChange={(e) => setCustomerEmailBody(e.target.value)}
                                rows={10}
                                className="w-full p-2 border rounded-md text-sm font-mono"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                Use placeholders:{' '}
                                <code className="bg-gray-100 px-1 rounded">{'{customerName}'}</code>{' '}
                                <code className="bg-gray-100 px-1 rounded">{'{jobTitle}'}</code>{' '}
                                <code className="bg-gray-100 px-1 rounded">{'{jobDate}'}</code>{' '}
                                <code className="bg-gray-100 px-1 rounded">{'{companyName}'}</code>
                            </p>
                        </div>

                        <div className="bg-gray-50 border rounded-md px-4 py-3 text-sm text-gray-600">
                            <span className="font-semibold text-gray-700">Email subject will be: </span>
                            Reminder: Your upcoming job with {'{'}{'{companyName}'}{'}'} 
                        </div>
                    </div>
                )}
            </div>

            {/* ── Save ────────────────────────────────────────────────────── */}
            <div className="flex items-center gap-4 border-t pt-6">
                <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="px-6 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition-colors"
                >
                    {isSaving ? 'Saving...' : 'Save Settings'}
                </button>
                {saveMessage && (
                    <span className={`text-sm font-medium ${saveMessage.includes('Error') ? 'text-red-600' : 'text-green-600'}`}>
                        {saveMessage}
                    </span>
                )}
            </div>
        </div>
    );
};

export default JobReminderSettings;